import React, { useState, useRef, useCallback } from 'react';
import { Loader2, ArrowDown } from 'lucide-react';

export function PullToRefresh({ onRefresh, children, className = '' }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);

  const THRESHOLD = 80; // Distance needed to trigger refresh
  const MAX_PULL = 120; // Maximum pull distance

  const handleTouchStart = useCallback((e) => {
    // Only enable pull-to-refresh when at the top of the scroll container
    if (containerRef.current?.scrollTop === 0 && !isRefreshing) {
      startY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, [isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || isRefreshing) return;
    
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;
    
    if (diff > 0 && containerRef.current?.scrollTop === 0) {
      // Apply resistance to make it feel natural
      const resistance = Math.min(diff * 0.5, MAX_PULL);
      setPullDistance(resistance);
      
      // Prevent default scroll when pulling
      if (resistance > 10) {
        e.preventDefault();
      }
    }
  }, [isPulling, isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;
    
    setIsPulling(false);
    
    if (pullDistance >= THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(60); // Keep indicator visible during refresh
      
      try {
        await onRefresh?.();
      } catch (err) {
        console.error('Refresh failed:', err);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [isPulling, pullDistance, isRefreshing, onRefresh]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const shouldShowIndicator = pullDistance > 10 || isRefreshing;

  return (
    <div
      ref={containerRef}
      className={`relative overflow-auto ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: isPulling && pullDistance > 10 ? 'none' : 'auto' }}
    >
      {/* Pull indicator */}
      <div
        className={`absolute left-0 right-0 flex justify-center z-20 transition-opacity duration-200 ${
          shouldShowIndicator ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          top: Math.max(pullDistance - 50, -50),
          transform: `translateY(${shouldShowIndicator ? '0' : '-20px'})`,
        }}
      >
        <div
          className={`flex items-center justify-center h-10 w-10 rounded-full bg-slate-800 border border-slate-700 shadow-lg transition-transform duration-200 ${
            isRefreshing ? 'scale-100' : ''
          }`}
          style={{
            transform: `rotate(${progress * 180}deg) scale(${0.8 + progress * 0.2})`,
          }}
        >
          {isRefreshing ? (
            <Loader2 className="h-5 w-5 text-[#0ea5e9] animate-spin" />
          ) : (
            <ArrowDown
              className={`h-5 w-5 transition-colors ${
                progress >= 1 ? 'text-[#0ea5e9]' : 'text-slate-400'
              }`}
            />
          )}
        </div>
      </div>

      {/* Content with pull offset */}
      <div
        className="transition-transform duration-200 ease-out"
        style={{
          transform: `translateY(${pullDistance}px)`,
          transitionDuration: isPulling ? '0ms' : '200ms',
        }}
      >
        {children}
      </div>

      {/* Pull hint text */}
      {shouldShowIndicator && !isRefreshing && (
        <div
          className="absolute left-0 right-0 text-center text-[10px] text-slate-500 font-mono uppercase tracking-wider transition-opacity"
          style={{ top: pullDistance + 8 }}
        >
          {progress >= 1 ? 'Release to refresh' : 'Pull to refresh'}
        </div>
      )}
    </div>
  );
}

export default PullToRefresh;
